<script setup>
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import CsmlOptionListEditor from './CsmlOptionListEditor.vue';

/**
 * Carousel cards drive the node's out-ports through their buttons, so the
 * buttons have to be editable here - a card with none can only ever generate
 * a comment where its branch should be.
 */
const props = defineProps({
  node: { type: Object, required: true },
});

const emit = defineEmits(['patch']);

const { t } = useI18n();

const cards = () => props.node.cards || [];

const updateCard = (index, patch) => {
  emit('patch', {
    cards: cards().map((card, i) =>
      i === index ? { ...card, ...patch } : card
    ),
  });
};

const removeCard = index => {
  emit('patch', { cards: cards().filter((_, i) => i !== index) });
};

const addCard = () => {
  const count = cards().length + 1;
  emit('patch', {
    cards: [
      ...cards(),
      {
        title: `${t('AGENT_BOTS.VISUAL_BUILDER.CARD')} ${count}`,
        subtitle: '',
        image_url: '',
        buttons: [
          {
            label: `${t('AGENT_BOTS.VISUAL_BUILDER.OPTION')} ${count}`,
            payload: `act${count}`,
          },
        ],
      },
    ],
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <label class="flex items-center gap-2 text-sm text-n-slate-12">
      <Checkbox
        :model-value="!!node.autoplay"
        @update:model-value="emit('patch', { autoplay: $event })"
      />
      {{ t('AGENT_BOTS.VISUAL_BUILDER.AUTOPLAY') }}
    </label>
    <div
      v-for="(card, idx) in node.cards || []"
      :key="`card-${idx}`"
      class="flex flex-col gap-2 p-2 border rounded-lg border-n-weak"
    >
      <div class="flex items-center justify-between">
        <span class="text-[10px] font-semibold uppercase text-n-slate-10">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.CARD') }} {{ idx + 1 }}
        </span>
        <Button
          size="xs"
          variant="ghost"
          color="ruby"
          icon="i-lucide-trash-2"
          is-icon-only
          :title="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
          @click="removeCard(idx)"
        />
      </div>
      <Input
        :model-value="card.title"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TITLE')"
        @update:model-value="updateCard(idx, { title: $event })"
      />
      <Input
        :model-value="card.subtitle"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.SUBTITLE')"
        @update:model-value="updateCard(idx, { subtitle: $event })"
      />
      <Input
        :model-value="card.image_url"
        size="sm"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.IMAGE_URL')"
        @update:model-value="updateCard(idx, { image_url: $event })"
      />
      <CsmlOptionListEditor
        :items="card.buttons || []"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.CARD_BUTTONS')"
        :add-label="t('AGENT_BOTS.VISUAL_BUILDER.ADD_BUTTON')"
        :payload-prefix="`card${idx}btn`"
        @update="updateCard(idx, { buttons: $event })"
      />
    </div>
    <Button
      size="sm"
      color="blue"
      variant="faded"
      icon="i-lucide-plus"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.ADD_CARD')"
      @click="addCard"
    />
  </div>
</template>
